import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { IconPlus } from '@/components/Icon'
import { cn } from '@/lib/cn'
import {
  EVENTO_VERSAO_NOVA,
  aplicarAtualizacao,
  instalarApp,
  observarInstalacao,
  rodandoInstalado,
} from '@/lib/pwa'

/**
 * Dois avisos do aplicativo instalável, discretos e dispensáveis.
 *
 * **Versão nova:** sem aviso, o usuário fica preso numa versão antiga até fechar
 * todas as abas — e conclui que a correção não foi feita. O aviso não recarrega
 * sozinho: interromper alguém no meio de um formulário para aplicar atualização é
 * pior que a versão antiga.
 *
 * **Instalação:** o convite aparece uma vez e, se recusado, não volta na mesma
 * sessão. Insistir em oferecer instalação irrita.
 */

export function AvisosPwa() {
  const [temVersaoNova, setTemVersaoNova] = useState(false)
  const [podeInstalar, setPodeInstalar] = useState(false)
  const [recusouInstalar, setRecusouInstalar] = useState(
    () => sessionStorage.getItem('autocomex:sem-convite-instalar') === '1',
  )

  useEffect(() => {
    const aoAtualizar = () => setTemVersaoNova(true)
    window.addEventListener(EVENTO_VERSAO_NOVA, aoAtualizar)
    const pararDeObservar = observarInstalacao(setPodeInstalar)
    return () => {
      window.removeEventListener(EVENTO_VERSAO_NOVA, aoAtualizar)
      pararDeObservar()
    }
  }, [])

  const mostrarInstalar = podeInstalar && !recusouInstalar && !rodandoInstalado()

  if (!temVersaoNova && !mostrarInstalar) return null

  return (
    <div
      className={cn(
        'fixed right-4 z-50 flex w-full max-w-sm flex-col gap-2',
        // Acima da barra inferior no celular.
        'bottom-20 lg:bottom-4',
      )}
    >
      {temVersaoNova && (
        <div className="rounded-lg border border-brand-200 bg-white p-3 shadow-md">
          <p className="text-sm font-medium text-ink-800">Nova versão disponível</p>
          <p className="mt-0.5 text-2xs text-ink-500">
            Recarregue quando terminar o que está fazendo — nada será perdido antes disso.
          </p>
          <div className="mt-2 flex gap-1.5">
            <Button size="sm" variant="primary" onClick={aplicarAtualizacao}>
              Recarregar agora
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setTemVersaoNova(false)}>
              Depois
            </Button>
          </div>
        </div>
      )}

      {mostrarInstalar && (
        <div className="rounded-lg border border-ink-200 bg-white p-3 shadow-md">
          <p className="text-sm font-medium text-ink-800">Instalar o AutoComex</p>
          <p className="mt-0.5 text-2xs text-ink-500">
            Abre em janela própria, sem barra do navegador, e fica no aparelho como um aplicativo.
          </p>
          <div className="mt-2 flex gap-1.5">
            <Button
              size="sm"
              variant="primary"
              icon={<IconPlus />}
              onClick={() => {
                void instalarApp().then((aceitou) => {
                  if (!aceitou) dispensar()
                })
              }}
            >
              Instalar
            </Button>
            <Button size="sm" variant="ghost" onClick={dispensar}>
              Não agora
            </Button>
          </div>
        </div>
      )}
    </div>
  )

  function dispensar() {
    setRecusouInstalar(true)
    try {
      sessionStorage.setItem('autocomex:sem-convite-instalar', '1')
    } catch {
      // Modo privado: aceitar que o convite volte.
    }
  }
}
